/**
 * The organizer public profile's pure functions and copy (`MEM-018`).
 *
 * Everything a test needs to hold lives here rather than in a component body: there is no DOM
 * renderer in this repository, so a rule buried in JSX is a rule with no test. That is the same
 * reason `event-discovery/constants.ts` exists, and the rules that matter most on this screen —
 * which route a profile is reached at, and how a date tile reads in the event's own timezone —
 * are exactly the kind that look right on one developer's laptop and are wrong for everybody
 * else.
 *
 * **No `@/` imports.** These are plain modules run by `node --test` with no bundler in front of
 * them, so an alias only Vite can resolve would make the file untestable — which is the one
 * property it exists for.
 */

import type { OrganizerProfileLink } from '@h2hspark/ui';

import type { DiscoverEventCard } from '../event-discovery/types';

// ─── the two addresses one profile has (`MEM-018` AC-6, and the parity report's §10) ──────────

/**
 * Where the profile lives **inside the member app**.
 *
 * ## The defect this exists to close
 *
 * The member-app parity pass found the organizer profile rendering in *marketing* chrome, so a
 * member who tapped "Hosted by …" on an event "loses the tab bar and the app shell". The
 * prototype treats it as a member-app screen with a back arrow returning to the event.
 *
 * ## Why two routes rather than one component sniffing its context
 *
 * The same profile is genuinely reachable two ways and the two readers are different people:
 *
 *   - `h2hspark.com/{slug}` — **anyone, signed out.** Public, indexable, marketing chrome. That
 *     is `MEM-018` AC-6 and `ORG-017` AC-3, it is the address an organizer shares, and it must
 *     keep resolving exactly as it does. {@link marketingProfilePath} is that one.
 *   - `/app/organizers/{slug}` — **a member who tapped the host on an event.** Inside
 *     `MemberLayout`, so the shell and the tab bar stay put.
 *
 * A single route deciding its own chrome would have to guess which reader it has, and the two
 * failure modes are both bad: a signed-out visitor served app chrome, or a member thrown out of
 * the app. Routing is the one thing that already knows, and React Router cannot express "this
 * path, in that shell" any other way. What is *not* duplicated is anything below the chrome —
 * both routes render one panel over one pair of queries, so the screens cannot drift.
 *
 * The marketing route also could not simply move: `/:slug` is resolved by `SlugRoutePage`, which
 * tries the organizer first and falls back to a published CMS page, and that precedence is a
 * client decision from 2026-08-22.
 */
export function memberProfilePath(slug: string): string {
  return `/app/organizers/${encodeURIComponent(slug)}`;
}

/**
 * Where a signed-out visitor's **Follow** goes (`MEM-018` AC-5, TW 44540823, Sumit 2026-10-28):
 * the member sign-in, with `?next=` set to this organizer's page in the app, where the toggle
 * is. The sign-in page already sanitises `next`, so this only has to name a member-app address.
 */
export function signedOutFollowPath(slug: string): string {
  return `/app/login?next=${encodeURIComponent(memberProfilePath(slug))}`;
}

/** Where a signed-out visitor reads the same profile — `h2hspark.com/{slug}` (`MEM-018` AC-6). */
export function marketingProfilePath(slug: string): string {
  return `/${encodeURIComponent(slug)}`;
}

/**
 * Where an event's "Hosted by {organizer}" links (`MEM-015` AC-3).
 *
 * **Into the app, not out of it.** This lived in `event-discovery/constants.ts` and returned
 * `/{slug}` — the marketing address — until `MEM-018`, and the parity pass found what that did:
 * a member reading an event taps the host and "loses the tab bar and the app shell". The event
 * detail page only ever renders inside `/app`, so the profile it points at is the one in the
 * shell.
 *
 * It lives **here** rather than beside the event card so that both of this profile's addresses
 * are decided in one file. `event-discovery/constants.ts` importing this one would have closed a
 * cycle — this file reads that one for the event card's own shapes — and a
 * one-way dependency in the direction the product runs (an event links to an organizer) is the
 * right way round.
 *
 * `null` when the organization has no slug, and then the name renders as plain text rather than
 * as a link to nowhere.
 */
export function hostProfilePath(
  detail: { readonly host: { readonly slug: string | null } | null },
): string | null {
  const slug = detail.host?.slug;
  return slug === undefined || slug === null ? null : memberProfilePath(slug);
}

// ─── the screen's copy (`MEM-018` AC-2, AC-3) ─────────────────────────────────────────────────

/**
 * The two section headings and the line under the first, from the prototype verbatim.
 *
 * Here rather than served by the API, unlike Discover's heading and search placeholder: those are
 * a *control vocabulary* the API composes because the atmosphere pills come from the taxonomy and
 * the empty-state sentence interpolates the server's own query. These two say nothing the server
 * knows. `MEM-014`'s greeting is on the same side of that line, for the same reason.
 */
export const PROFILE_COPY = {
  upcomingHeading: 'Upcoming events',
  pastHeading: 'Past events',
  /**
   * What an organizer with nothing coming up says.
   *
   * Not "0 events" and not an empty space. An empty region reads as a broken page, and a zero
   * reads as a judgement on the organizer — this says what is true, which is that the calendar is
   * empty *right now*.
   */
  noUpcoming: 'Nothing on the calendar right now. This is what is published, not what exists.',
  /** And with no history. A new organizer is a normal state, not a deficiency. */
  noPast: 'No past events yet.',
  /**
   * The heading over the organizer's own words (`MEM-018` AC-1).
   *
   * Not drawn any more: the 10 September header (§2.8) puts the introduction **inside** it, under
   * the meta row, where it needs no heading of its own. Kept as the accessible name for the
   * paragraph should a screen ever need to label it, and as the record of where the section went.
   */
  aboutHeading: 'About',
  /**
   * The link above the header, at both of the profile's addresses (`Web App.dc.html` §2.8, Brad's
   * design of 27 October, `MEM-060` AC-5).
   */
  backToEvents: 'Back to events',
  /** Beside Follow, in both states (Brad's design of 27 October, `MEM-018` AC-1, AC-5). */
  followCaption: 'Hear when they publish an event',
  /** A signed-out visitor's Follow, which is a link to sign in rather than the toggle (AC-5). */
  signedOutFollowLabel: 'Follow',
  /**
   * The gallery's heading (`ORG-018` AC-1) — the same words the organizer sees over the control
   * that fills it, because a member should recognise what they are looking at as the thing the
   * organizer chose to show them.
   */
  galleryHeading: 'Photos from their events',
  /** An upcoming row's tags past the first three (`MEM-060` AC-6) — *"+3 more"*. */
  moreTags: (count: number): string => `+${String(count)} more`,
  /** An upcoming row's button (`MEM-060` AC-6). */
  viewEvent: 'View event',
  /**
   * A past row's attendance — *"42 attended"* (`MEM-060` AC-7). Only ever called with a count the
   * API published, which it does from five checked in; this never renders a smaller number.
   */
  attended: (count: number): string => `${String(count)} attended`,
  /** Beside the heading, as Brad's design of 27 October draws it — *"9 photos"* (`MEM-060` AC-8). */
  galleryCount: (count: number): string => `${String(count)} ${count === 1 ? 'photo' : 'photos'}`,
  /**
   * A gallery image's text alternative.
   *
   * `ORG-018` collects no description per image — unlike an event cover, which `EVT-008` AC-2
   * gives a description field precisely so it has one. So the alternative says what the image is
   * and whose it is, which is the honest maximum from what is stored, and it is a real sentence
   * rather than an empty `alt` because these images are content on the page and not decoration
   * (NFR accessibility#wcag-aa). Giving each image a description of its own is an intake, not a
   * thing to invent here.
   */
  galleryImageAlt: (organizerName: string, position: number, total: number): string =>
    `Photo ${String(position)} of ${String(total)} from ${organizerName}'s events`,
} as const;

/**
 * The label for the counts line under each heading.
 *
 * A count of the **organizer's own events** — which is what `MEM-018` AC-7 permits ("counts and
 * the organizer's own content only") and is a different thing from the prototype's `412 members
 * hosted` and `68% return rate`, neither of which is served and neither of which could be: an
 * attendance aggregate with no five-member floor under it is exactly what the member data
 * boundary withholds.
 */
export function eventCountLabel(count: number): string {
  return count === 1 ? '1 event' : `${String(count)} events`;
}

// ─── the date tile (`MEM-018` AC-2: "date, name, day and time") ────────────────────────────────

export interface EventDateTile {
  /** `JAN` — the prototype renders it uppercase; the value is uppercased here so a test can hold
   * the string the screen shows rather than the one CSS would. */
  readonly month: string;
  /** `14`, zero-padded to two digits as the prototype's own data is. */
  readonly day: string;
}

/**
 * The month/day tile beside every row, **in the event's own timezone**.
 *
 * `MEM-015` AC-2's rule reaching a second surface: an event at 8:30am on the 14th in Providence
 * is on the 14th for every reader, and a tile built from the device's clock would show the 13th
 * to somebody in Los Angeles. The zone is the event's; there is deliberately **no fallback to the
 * reader's** — an event with no stored zone renders in UTC, matching
 * `event-discovery/constants.ts`, because a wrong-but-plausible local date is worse than a
 * consistent one.
 *
 * `null` for an unparseable instant or an unresolvable zone, so a caller draws nothing rather
 * than `Invalid Date`. Same shape and same reason as `formatTicketDateTime`.
 */
export function eventDateTile(input: {
  readonly startsAt: string;
  readonly timezone: string | null;
}): EventDateTile | null {
  const start = new Date(input.startsAt);
  if (Number.isNaN(start.getTime())) return null;
  try {
    const zone = input.timezone ?? 'UTC';
    const month = new Intl.DateTimeFormat('en-US', { timeZone: zone, month: 'short' }).format(
      start,
    );
    const day = new Intl.DateTimeFormat('en-US', { timeZone: zone, day: '2-digit' }).format(start);
    return { month: month.toUpperCase(), day };
  } catch {
    return null;
  }
}

/**
 * **The row's second line moved to `packages/ui`**, and this records where.
 *
 * `eventDayAndTime` lived here and returned the prototype's `Thu · 8:30 AM`, with the zone's
 * abbreviation appended only when it differs from the reader's. `formatEventWeekdayTime` in
 * `@h2hspark/ui` is the same rule, character for character, extracted when the member app's
 * screens started sharing an event card; the upcoming list now renders `event-discovery`'s
 * `EventCard`, which calls it. Two implementations of one rule is how two surfaces that draw the
 * same event's time end up disagreeing, so the local copy is gone rather than left unused.
 */

/**
 * A past row's second line — the prototype's `{city} · {n} attended`, **without the count**.
 *
 * The count is the deliberate omission, and it is `MEM-018` AC-7 rather than an oversight: `{n}
 * attended` is an aggregate over one room with no floor under it, and
 * `security-invariants#member-data-boundary` withholds an aggregate contributed to by fewer than
 * five members entirely rather than rounding it. The API does not serve the figure — it is not
 * among `DISCOVER_CARD_FIELDS` — so this could not render it even if a future edit wanted to.
 *
 * `null` when the event carries no city, so the row draws the name alone rather than a stray
 * separator.
 */
export function pastEventLocationLine(card: Pick<DiscoverEventCard, 'city' | 'region'>): string | null {
  const parts = [card.city, card.region].filter(
    (part): part is string => typeof part === 'string' && part.trim().length > 0,
  );
  return parts.length === 0 ? null : parts.join(', ');
}

/**
 * **The organizer's links, as the header's icon row needs them** (Sumit, 2026-09-12:
 * *"actually let's add social and web icon on the line below name and link those to open in new
 * tab."*).
 *
 * ## Which channels exist, and which shape is authoritative
 *
 * The public payload carries `website` plus a `socialLinks` map keyed `linkedIn`, `instagram`,
 * `facebook` and `x` — **four social channels and a website**, and that map is the authority. The
 * organizer *setup* flow collects only the website and LinkedIn, and Settings → Profile collects
 * all four; they are two forms writing one field, not two shapes. So a profile can hold a channel
 * the setup flow never asked for, and this renders whatever is actually there rather than
 * whichever form the organizer happened to use.
 *
 * ## Order, and why it is fixed here
 *
 * Website first, then LinkedIn, Instagram, Facebook, X. A map has no order worth trusting across a
 * serialisation, and a row of icons that reshuffled between two loads of the same page would look
 * like a bug. The order is the one the organizer's own settings form lists them in.
 *
 * A channel with an empty or blank value is dropped rather than drawn as a dead icon. `ORG-008`
 * AC-4 normalises these to URLs on save, so what arrives is an address rather than a handle.
 */
export function organizerLinks(profile: {
  readonly organizerName: string;
  readonly website: string | null;
  readonly socialLinks: Readonly<Partial<Record<'linkedIn' | 'instagram' | 'facebook' | 'x', string>>>;
}): ReadonlyArray<OrganizerProfileLink> {
  const named: ReadonlyArray<{
    readonly channel: OrganizerProfileLink['channel'];
    readonly href: string | undefined | null;
    readonly label: string;
  }> = [
    { channel: 'website', href: profile.website, label: `${profile.organizerName} website` },
    {
      channel: 'linkedIn',
      href: profile.socialLinks.linkedIn,
      label: `${profile.organizerName} on LinkedIn`,
    },
    {
      channel: 'instagram',
      href: profile.socialLinks.instagram,
      label: `${profile.organizerName} on Instagram`,
    },
    {
      channel: 'facebook',
      href: profile.socialLinks.facebook,
      label: `${profile.organizerName} on Facebook`,
    },
    { channel: 'x', href: profile.socialLinks.x, label: `${profile.organizerName} on X` },
  ];

  return named.flatMap((link) =>
    typeof link.href === 'string' && link.href.trim().length > 0
      ? [{ channel: link.channel, href: link.href.trim(), label: link.label }]
      : [],
  );
}

/**
 * **An upcoming row's tags** (`MEM-060` AC-6, Brad's design of 27 October): the first three, and
 * how many more there are. Three, because the row is one line in the design and the fourth chip is
 * where it wraps.
 */
export function upcomingRowTags<Tag>(tags: ReadonlyArray<Tag>): {
  readonly shown: ReadonlyArray<Tag>;
  readonly more: number;
} {
  const shown = tags.slice(0, 3);
  return { shown, more: tags.length - shown.length };
}
